/**
 * PresetSelector Component
 * 
 * Dropdown selector for effect presets.
 * Allows users to quickly load factory presets for effects.
 */

import { useState } from 'react';
import { getPresetsForEffect } from '../../../constants/effectPresets';
import './PresetSelector.css';

export interface PresetSelectorProps {
  /** Effect type */
  effectType: string;
  /** Callback when preset is selected */
  onPresetSelect: (presetParameters: Record<string, number>) => void;
  /** Whether selector is disabled */
  disabled?: boolean;
}

/**
 * PresetSelector component
 * 
 * @param props - Component props
 * @returns PresetSelector component
 */
export function PresetSelector({
  effectType,
  onPresetSelect,
  disabled = false,
}: PresetSelectorProps) {
  const [selectedPreset, setSelectedPreset] = useState<string>('');
  const presets = getPresetsForEffect(effectType);

  // Don't render if no presets available
  if (presets.length === 0) {
    return null;
  }

  const handlePresetChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const presetName = event.target.value;
    if (presetName === '') return;

    const preset = presets.find(p => p.name === presetName);
    console.log('Selected preset:', presetName, preset);
    if (preset) {
      setSelectedPreset(presetName);
      console.log('Calling onPresetSelect with:', preset.parameters);
      onPresetSelect(preset.parameters);
    }
  };

  return (
    <div className="preset-selector">
      <label htmlFor="preset-select" className="preset-selector-label">
        Preset:
      </label>
      <select
        id="preset-select"
        className="preset-selector-dropdown"
        onChange={handlePresetChange}
        disabled={disabled}
        value={selectedPreset}
      >
        <option value="">
          {selectedPreset ? selectedPreset : 'Load Preset...'}
        </option>
        {presets.map((preset) => (
          <option key={preset.name} value={preset.name} title={preset.description}>
            {preset.name}
          </option>
        ))}
      </select>
    </div>
  );
}
